import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Payment Tracking Page (Level 5.4)
 *
 * Manage both A/R (customer payments) and A/P (supplier payments)
 * Record payments, update status, track pending verifications
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiClient from '../../utils/apiClient';
import { notify } from '../../utils/notifications';
import BaseModal from '../../components/shared/BaseModal';
import { PAYMENT_STATUS_LABELS, PAYMENT_STATUS_COLORS } from '../../types/purchase';
import { SUPPLIER_PAYMENT_STATUS_LABELS, SUPPLIER_PAYMENT_STATUS_COLORS, SUPPLIER_PAYMENT_METHOD_LABELS } from '../../types/payment';
import TabPageLayout from './FinancialHub/TabPageLayout';
import { Button, StatsGrid, StatCard, StatLabel, StatValue, TableContainer, Table, Th, Td, EmptyState, EmptyStateIcon, EmptyStateText } from './FinancialHub/SharedComponents';
const PaymentTracking = () => {
    const [activeTab, setActiveTab] = useState('supplier');
    const [loading, setLoading] = useState(false);
    // Supplier Payments (A/P)
    const [supplierPayments, setSupplierPayments] = useState([]);
    const [unpaidPurchases, setUnpaidPurchases] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [showSupplierPaymentModal, setShowSupplierPaymentModal] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    // Customer Payments (A/R)
    const [customerPayments, setCustomerPayments] = useState([]);
    const [unpaidInvoices, setUnpaidInvoices] = useState([]);
    const [showCustomerPaymentModal, setShowCustomerPaymentModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    // Statistics
    const [supplierStats, setSupplierStats] = useState(null);
    const [customerStats, setCustomerStats] = useState(null);
    // Supplier Payment Form
    const [supplierPaymentForm, setSupplierPaymentForm] = useState({
        amount: 0,
        paymentMethod: 'wire_transfer',
        paymentReference: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paidInFull: false,
        notes: ''
    });
    // Customer Payment Form
    const [customerPaymentForm, setCustomerPaymentForm] = useState({
        amount: 0,
        paymentMethod: 'wire_transfer',
        paymentReference: '',
        paymentDate: new Date().toISOString().split('T')[0],
        status: 'pending',
        notes: ''
    });
    // Fetch unpaid purchases
    const fetchUnpaidPurchases = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/purchases-list?paymentStatus=unpaid');
            if (response.ok && response.data) {
                setUnpaidPurchases(response.data.purchases || []);
            }
            // Also fetch partial payments
            const partialResponse = await apiClient.get('/purchases-list?paymentStatus=partial');
            if (partialResponse.ok && partialResponse.data) {
                const combined = [...(response.data?.purchases || []), ...(partialResponse.data?.purchases || [])];
                setUnpaidPurchases(combined);
            }
        }
        catch (err) {
            console.error('Failed to fetch purchases:', err);
        }
        finally {
            setLoading(false);
        }
    };
    // Fetch supplier payments
    const fetchSupplierPayments = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/supplier-payments-list');
            if (response.ok && response.data) {
                setSupplierPayments(response.data.payments || []);
                setSupplierStats(response.data.statistics);
            }
        }
        catch (err) {
            notify.error('Error', 'Failed to fetch supplier payments');
        }
        finally {
            setLoading(false);
        }
    };
    // Fetch suppliers
    const fetchSuppliers = async () => {
        try {
            const response = await apiClient.get('/suppliers-list?status=active');
            if (response.ok && response.data) {
                setSuppliers(response.data.suppliers || []);
            }
        }
        catch (err) {
            console.error('Failed to fetch suppliers:', err);
        }
    };
    // Record supplier payment
    const recordSupplierPayment = async () => {
        if (!selectedPurchase)
            return;
        setLoading(true);
        try {
            const paymentData = {
                supplierId: selectedPurchase.supplierId,
                referenceId: selectedPurchase.id,
                referenceType: 'purchase',
                referenceNumber: selectedPurchase.purchaseNumber,
                paymentFor: 'purchase',
                amount: supplierPaymentForm.amount,
                paymentMethod: supplierPaymentForm.paymentMethod,
                paymentReference: supplierPaymentForm.paymentReference,
                paymentDate: supplierPaymentForm.paymentDate,
                paidInFull: supplierPaymentForm.paidInFull,
                possessionTypeAfterPayment: supplierPaymentForm.paidInFull ? 'owned' : undefined,
                status: 'cleared', // Default to cleared (manual verification)
                notes: supplierPaymentForm.notes
            };
            const response = await apiClient.post('/supplier-payments-create', paymentData);
            if (response.ok && response.data) {
                const diamondsUpdated = response.data.diamondsUpdated || 0;
                const message = supplierPaymentForm.paidInFull && diamondsUpdated > 0
                    ? `Payment recorded: ${response.data.paymentNumber} | ${diamondsUpdated} diamond(s) now owned`
                    : `Payment recorded: ${response.data.paymentNumber}`;
                notify.success('Success', message);
                setShowSupplierPaymentModal(false);
                setSelectedPurchase(null);
                resetSupplierPaymentForm();
                fetchUnpaidPurchases();
                fetchSupplierPayments();
            }
            else {
                notify.error('Error', response.error || 'Failed to record payment');
            }
        }
        catch (err) {
            notify.error('Error', err.message || 'Failed to record payment');
        }
        finally {
            setLoading(false);
        }
    };
    // Open supplier payment modal
    const openSupplierPaymentModal = (purchase) => {
        setSelectedPurchase(purchase);
        setSupplierPaymentForm({
            amount: purchase.amountOwed, // Default to full amount owed
            paymentMethod: 'wire_transfer',
            paymentReference: '',
            paymentDate: new Date().toISOString().split('T')[0],
            paidInFull: purchase.amountOwed === purchase.total, // Auto-check if paying full amount
            notes: ''
        });
        setShowSupplierPaymentModal(true);
    };
    // Reset forms
    const resetSupplierPaymentForm = () => {
        setSupplierPaymentForm({
            amount: 0,
            paymentMethod: 'wire_transfer',
            paymentReference: '',
            paymentDate: new Date().toISOString().split('T')[0],
            paidInFull: false,
            notes: ''
        });
    };
    // Load data on mount and when tab changes
    useEffect(() => {
        if (activeTab === 'supplier') {
            fetchUnpaidPurchases();
            fetchSupplierPayments();
            fetchSuppliers();
        }
    }, [activeTab]);
    // Actions Section - only show for active tab
    const actionsSection = activeTab === 'supplier' ? (_jsx(Button, { onClick: fetchUnpaidPurchases, disabled: loading, children: loading ? 'Loading...' : 'Refresh' })) : null;
    return (_jsxs(TabPageLayout, { title: "Payment Tracking", subtitle: "Manage supplier payments (A/P) and customer payments (A/R) with manual verification", actions: actionsSection, children: [_jsxs(TabContainer, { children: [_jsx(Tab, { active: activeTab === 'supplier', onClick: () => setActiveTab('supplier'), children: "Supplier Payments (A/P - We Pay)" }), _jsx(Tab, { active: activeTab === 'customer', onClick: () => setActiveTab('customer'), children: "Customer Payments (A/R - We Receive)" })] }), activeTab === 'supplier' && (_jsxs(_Fragment, { children: [supplierStats && (_jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsx(StatLabel, { children: "Total Payments" }), _jsx(StatValue, { children: supplierStats.total })] }), _jsxs(StatCard, { children: [_jsx(StatLabel, { children: "Pending" }), _jsx(StatValue, { style: { color: '#f59e0b' }, children: supplierStats.byStatus?.pending || 0 })] }), _jsxs(StatCard, { children: [_jsx(StatLabel, { children: "Total Paid" }), _jsxs(StatValue, { style: { color: '#10b981' }, children: ["$", supplierStats.totalSent?.toLocaleString() || 0] })] }), _jsxs(StatCard, { children: [_jsx(StatLabel, { children: "Pending Amount" }), _jsxs(StatValue, { style: { color: '#ef4444' }, children: ["$", supplierStats.totalPending?.toLocaleString() || 0] })] })] })), _jsxs(Section, { children: [_jsxs(SectionHeader, { children: [_jsxs(SectionTitle, { children: ["Unpaid Purchases (", unpaidPurchases.length, ")"] }), _jsx(Button, { onClick: fetchUnpaidPurchases, disabled: loading, children: loading ? 'Loading...' : 'Refresh' })] }), _jsxs(TableContainer, { children: [_jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Purchase #" }), _jsx(Th, { children: "Date" }), _jsx(Th, { children: "Supplier" }), _jsx(Th, { children: "Type" }), _jsx(Th, { children: "Total" }), _jsx(Th, { children: "Paid" }), _jsx(Th, { children: "Owed" }), _jsx(Th, { children: "Status" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: unpaidPurchases.map((purchase) => (_jsxs("tr", { children: [_jsx(Td, { children: _jsx(Code, { children: purchase.purchaseNumber }) }), _jsx(Td, { children: new Date(purchase.purchaseDate).toLocaleDateString() }), _jsxs(Td, { children: [_jsx("strong", { children: purchase.supplierCode }), _jsx("br", {}), _jsx("small", { children: purchase.supplierName })] }), _jsx(Td, { children: purchase.purchaseType }), _jsxs(Td, { children: ["$", purchase.total.toLocaleString()] }), _jsxs(Td, { style: { color: '#10b981' }, children: ["$", purchase.amountPaid.toLocaleString()] }), _jsxs(Td, { style: { color: '#ef4444', fontWeight: 600 }, children: ["$", purchase.amountOwed.toLocaleString()] }), _jsx(Td, { children: _jsx(Badge, { color: PAYMENT_STATUS_COLORS[purchase.paymentStatus], children: PAYMENT_STATUS_LABELS[purchase.paymentStatus] }) }), _jsx(Td, { children: _jsx(Button, { "$primary": true, onClick: () => openSupplierPaymentModal(purchase), style: { padding: '0.5rem 1rem', fontSize: '0.875rem' }, children: "Record Payment" }) })] }, purchase.id))) })] }), unpaidPurchases.length === 0 && !loading && (_jsxs(EmptyState, { children: [_jsx(EmptyStateIcon, {}), _jsx(EmptyTitle, { children: "All purchases paid!" }), _jsx(EmptyStateText, { children: "No outstanding payments to suppliers" })] }))] })] }), _jsxs(Section, { children: [_jsxs(SectionTitle, { children: ["Recent Payments (", supplierPayments.length, ")"] }), _jsxs(TableContainer, { children: [_jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Payment #" }), _jsx(Th, { children: "Date" }), _jsx(Th, { children: "Supplier" }), _jsx(Th, { children: "Purchase #" }), _jsx(Th, { children: "Amount" }), _jsx(Th, { children: "Method" }), _jsx(Th, { children: "Status" }), _jsx(Th, { children: "Paid in Full" })] }) }), _jsx("tbody", { children: supplierPayments.slice(0, 10).map((payment) => (_jsxs("tr", { children: [_jsx(Td, { children: _jsx(Code, { children: payment.paymentNumber }) }), _jsx(Td, { children: new Date(payment.paymentDate).toLocaleDateString() }), _jsx(Td, { children: payment.supplierName }), _jsx(Td, { children: payment.referenceNumber || 'N/A' }), _jsxs(Td, { children: ["$", payment.amount.toLocaleString()] }), _jsx(Td, { children: SUPPLIER_PAYMENT_METHOD_LABELS[payment.paymentMethod] }), _jsx(Td, { children: _jsx(Badge, { color: SUPPLIER_PAYMENT_STATUS_COLORS[payment.status], children: SUPPLIER_PAYMENT_STATUS_LABELS[payment.status] }) }), _jsx(Td, { children: payment.paidInFull ? 'Yes' : 'Partial' })] }, payment.id))) })] }), supplierPayments.length === 0 && !loading && (_jsxs(EmptyState, { children: [_jsx(EmptyStateIcon, {}), _jsx(EmptyTitle, { children: "No payment history" }), _jsx(EmptyStateText, { children: "Record your first supplier payment above" })] }))] })] })] })), activeTab === 'customer' && (_jsxs(EmptyState, { children: [_jsx(EmptyStateIcon, { children: "\uD83D\uDCB3" }), _jsx(EmptyTitle, { children: "Customer Payments (A/R)" }), _jsx(EmptyStateText, { children: "Invoice payment tracking is in development." }), _jsxs("ul", { style: { textAlign: 'left', margin: '16px auto', maxWidth: '280px', color: 'var(--text-secondary)', fontSize: '0.875rem' }, children: [_jsx("li", { style: { marginBottom: '8px' }, children: "Track customer invoice payments" }), _jsx("li", { style: { marginBottom: '8px' }, children: "Payment reminders & notifications" }), _jsx("li", { style: { marginBottom: '8px' }, children: "Aging reports for outstanding invoices" }), _jsx("li", { style: { marginBottom: '8px' }, children: "Payment history & reconciliation" })] }), _jsx(EmptyStateText, { style: { fontSize: '0.8rem', color: 'var(--text-tertiary)' }, children: "Use the Supplier Payments tab for A/P tracking." })] })), _jsx(BaseModal, { isOpen: showSupplierPaymentModal, onClose: () => {
                    setShowSupplierPaymentModal(false);
                    setSelectedPurchase(null);
                    resetSupplierPaymentForm();
                }, title: `Record Payment to Supplier`, maxWidth: "600px", footer: _jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => {
                                setShowSupplierPaymentModal(false);
                                setSelectedPurchase(null);
                                resetSupplierPaymentForm();
                            }, children: "Cancel" }), _jsx(Button, { onClick: recordSupplierPayment, "$primary": true, disabled: loading || !supplierPaymentForm.amount || !supplierPaymentForm.paymentReference, children: loading ? 'Recording...' : 'Record Payment' })] }), children: selectedPurchase && (_jsxs(_Fragment, { children: [_jsxs(InfoBox, { children: [_jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Purchase:" }), _jsx(InfoValue, { children: _jsx(Code, { children: selectedPurchase.purchaseNumber }) })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Supplier:" }), _jsx(InfoValue, { children: _jsx("strong", { children: selectedPurchase.supplierName }) })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Purchase Total:" }), _jsxs(InfoValue, { children: ["$", selectedPurchase.total.toLocaleString()] })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Already Paid:" }), _jsxs(InfoValue, { style: { color: '#10b981' }, children: ["$", selectedPurchase.amountPaid.toLocaleString()] })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Amount Owed:" }), _jsxs(InfoValue, { style: { color: '#ef4444', fontWeight: 600 }, children: ["$", selectedPurchase.amountOwed.toLocaleString()] })] })] }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { fullWidth: true, children: [_jsx(Label, { children: "Payment Amount *" }), _jsx(Input, { type: "number", step: "0.01", value: supplierPaymentForm.amount || '', onChange: (e) => {
                                                const amount = parseFloat(e.target.value) || 0;
                                                setSupplierPaymentForm({
                                                    ...supplierPaymentForm,
                                                    amount,
                                                    paidInFull: amount >= selectedPurchase.amountOwed
                                                });
                                            }, placeholder: "8553.28" }), _jsxs("small", { style: { color: '#666' }, children: ["Max: $", selectedPurchase.amountOwed.toLocaleString()] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Payment Method *" }), _jsxs(Select, { value: supplierPaymentForm.paymentMethod, onChange: (e) => setSupplierPaymentForm({ ...supplierPaymentForm, paymentMethod: e.target.value }), children: [_jsx("option", { value: "wire_transfer", children: "Wire Transfer" }), _jsx("option", { value: "check", children: "Check" }), _jsx("option", { value: "ach", children: "ACH Transfer" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Payment Date *" }), _jsx(Input, { type: "date", value: supplierPaymentForm.paymentDate, onChange: (e) => setSupplierPaymentForm({ ...supplierPaymentForm, paymentDate: e.target.value }) })] }), _jsxs(FormGroup, { fullWidth: true, children: [_jsx(Label, { children: "Payment Reference * (Check #, Wire Confirmation, etc.)" }), _jsx(Input, { type: "text", value: supplierPaymentForm.paymentReference, onChange: (e) => setSupplierPaymentForm({ ...supplierPaymentForm, paymentReference: e.target.value }), placeholder: "CHECK-12345 or WIRE-ABC123" })] }), _jsx(FormGroup, { fullWidth: true, children: _jsxs(CheckboxLabel, { children: [_jsx("input", { type: "checkbox", checked: supplierPaymentForm.paidInFull, onChange: (e) => setSupplierPaymentForm({ ...supplierPaymentForm, paidInFull: e.target.checked }) }), _jsxs("span", { children: [_jsx("strong", { children: "Paid in Full" }), selectedPurchase.purchaseType === 'memo' && (_jsx("small", { style: { color: '#10b981', marginLeft: '0.5rem' }, children: "(Will change diamond possession to \"owned\")" }))] })] }) }), _jsxs(FormGroup, { fullWidth: true, children: [_jsx(Label, { children: "Notes" }), _jsx(Textarea, { value: supplierPaymentForm.notes, onChange: (e) => setSupplierPaymentForm({ ...supplierPaymentForm, notes: e.target.value }), placeholder: "Additional notes...", rows: 3 })] })] }), supplierPaymentForm.paidInFull && selectedPurchase.purchaseType === 'memo' && (_jsx(InfoBox, { style: { background: '#dcfce7', borderColor: '#22c55e' }, children: _jsxs("div", { style: { color: '#15803d', fontWeight: 500 }, children: ["Marking as \"Paid in Full\" will change ", selectedPurchase.items.length, " diamond(s) from \"memo\" to \"owned\"!"] }) }))] })) })] }));
};
// Component-specific styled components (keep internal tabs, section, modal, and form components)
const TabContainer = styled.div `
    display: flex;
    gap: 0.5rem;
    margin-bottom: 2rem;
    border-bottom: 2px solid var(--border-color, #e5e7eb);
`;
const Tab = styled.button `
    padding: 1rem 2rem;
    border: none;
    background: ${props => props.active ? 'var(--card-bg, white)' : 'transparent'};
    color: ${props => props.active ? 'var(--text-primary, #1a1a1a)' : 'var(--text-secondary, #666)'};
    font-weight: ${props => props.active ? 600 : 400};
    font-size: 1rem;
    cursor: pointer;
    border-bottom: 3px solid ${props => props.active ? '#3b82f6' : 'transparent'};
    transition: all 0.2s;
    position: relative;
    top: 2px;

    &:hover {
        background: var(--card-bg, white);
        color: var(--text-primary, #1a1a1a);
    }
`;
const Section = styled.div `
    margin-bottom: 2rem;

    &:last-child {
        margin-bottom: 0;
    }
`;
const SectionHeader = styled.div `
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
`;
const SectionTitle = styled.h2 `
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary, #1a1a1a);
    margin: 0;
`;
const Code = styled.code `
    background: #f3f4f6;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.8rem;
`;
const Badge = styled.span `
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    background: ${props => props.color || '#e5e7eb'}22;
    color: ${props => props.color || '#6b7280'};
    border: 1px solid ${props => props.color || '#6b7280'}44;
`;
const EmptyTitle = styled.h3 `
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary, #1a1a1a);
    margin: 0 0 0.5rem 0;
`;
const InfoBox = styled.div `
    background: #eff6ff;
    border: 1px solid #93c5fd;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.5rem;
`;
const InfoRow = styled.div `
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px solid #dbeafe;

    &:last-child {
        border-bottom: none;
    }
`;
const InfoLabel = styled.div `
    font-size: 0.875rem;
    color: #1e40af;
    font-weight: 500;
`;
const InfoValue = styled.div `
    font-size: 0.875rem;
    color: var(--text-primary, #1a1a1a);
`;
const FormGrid = styled.div `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
`;
const FormGroup = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    grid-column: ${props => props.fullWidth ? '1 / -1' : 'auto'};
`;
const Label = styled.label `
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary, #1a1a1a);
`;
const Input = styled.input `
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color, #d1d5db);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--input-bg, white);
    color: var(--text-primary, #1a1a1a);

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &:disabled {
        background: #f3f4f6;
        cursor: not-allowed;
    }
`;
const Select = styled.select `
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color, #d1d5db);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--input-bg, white);
    color: var(--text-primary, #1a1a1a);
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
`;
const Textarea = styled.textarea `
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color, #d1d5db);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--input-bg, white);
    color: var(--text-primary, #1a1a1a);
    resize: vertical;
    font-family: inherit;

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
`;
const CheckboxLabel = styled.label `
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 6px;
    border: 2px solid var(--border-color, #e5e7eb);
    transition: all 0.2s;

    &:hover {
        border-color: #3b82f6;
        background: #eff6ff;
    }

    input[type="checkbox"] {
        width: 20px;
        height: 20px;
        cursor: pointer;
    }

    span {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }
`;
export default PaymentTracking;
